import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@progress/kendo-react-buttons";
import { Typography } from "@progress/kendo-react-common";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import ShadowCard from "../../components/common/ShadowCard";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import FormTextField from "../../components/formFields/FormTextField";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import { STATUSARRAY } from "../../_contstants/common";
import FormImageUpload from "../../components/formFields/FormImageUpload";
import {
  BlogImageUpload,
  createBlog,
  updateBlog,
  getBlogByID,
} from "./services/blog.services";
import FormDatePicker from "../../components/formFields/FormDateField";
import moment from "moment";
import FormRichTextField from "../../components/formFields/FormRichTextField";
import FormTextArea from "../../components/formFields/FormTextArea";
import { getFile, slugify } from "../../_helper/helper";

interface BlogDetailsFormProps {
  onSuccess: (BlogID: number) => void;
}

const BlogDetailsForm: React.FC<BlogDetailsFormProps> = ({ onSuccess }) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const BlogDetail = useAppSelector((state) => state.blog.BlogDetail);
  const BlogID = useAppSelector((state) => state.blog.BlogID);
  const loading = useAppSelector((state) => state.blog.loading);
  const [formKey, setFormKey] = useState(1);

  useEffect(() => {
    setFormKey((prev) => prev + 1);
  }, [BlogDetail]);

  const handleSubmit = async (values: any) => {
    const formData = {
      Title: values.Title ?? "",
      ShortDescription: values.ShortDescription ?? "",
      Keyword: values.Keyword ?? "",
      Description: values.Description ?? "",
      MetaTitle: values.MetaTitle ?? "",
      MetaDescription: values.MetaDescription ?? "",
      BlogURL: values.BlogURL ? values.BlogURL : "",
      BlogDate: values.BlogDate
        ? moment(values.BlogDate).format("YYYY-MM-DD")
        : null,
      IsActive: values.IsActive === 1,
    };
    const ImageData = new FormData();
    const Image =
      values?.Image && typeof values?.Image !== "string"
        ? getFile(values?.Image)
        : "";
    const BlogBannerImage =
      values?.BlogBannerImage && typeof values?.BlogBannerImage !== "string"
        ? getFile(values?.BlogBannerImage)
        : "";
    try {
      if (BlogID) {
        const editPayload = {
          ...formData,
          ID: BlogID,
        };

        const response = await dispatch(updateBlog(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          if (
            (values?.Image && typeof values?.Image !== "string") ||
            (values?.BlogBannerImage &&
              typeof values?.BlogBannerImage !== "string")
          ) {
            ImageData.append("Image", Image);
            ImageData.append("BlogBannerImage", BlogBannerImage);
            ImageData.append("ID", BlogID.toString());
            await dispatch(BlogImageUpload(ImageData));
          }
          dispatch(getBlogByID(BlogID));
          onSuccess(BlogID);
        }
      } else {
        const response = await dispatch(createBlog(formData));
        if (response?.meta?.requestStatus === "fulfilled") {
          const newBlogID = response?.payload?.Data?.ID;
          if (
            ((values?.Image && typeof values?.Image !== "string") ||
              (values?.BlogBannerImage &&
                typeof values?.BlogBannerImage !== "string")) &&
            newBlogID
          ) {
            ImageData.append("Image", Image);
            ImageData.append("BlogBannerImage", BlogBannerImage);
            ImageData.append("ID", newBlogID.toString());
            await dispatch(BlogImageUpload(ImageData));
          }
          if (newBlogID) {
            onSuccess(newBlogID);
          } else {
            navigate("/blog");
          }
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleCreateSlug = (e: any, formRenderProps: FormRenderProps) => {
    const slug = slugify(e.value);
    formRenderProps.onChange("BlogURL", { value: slug });
  };

  return (
    <div>
      <Form
        onSubmit={handleSubmit}
        key={formKey}
        initialValues={BlogDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement>
            <ShadowCard style={{ padding: 12 }}>
              <GridLayout
                style={{ marginRight: 30 }}
                gap={{ rows: 5, cols: 10 }}
                cols={[
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                  { width: "25%" },
                ]}
              >
                <GridLayoutItem colSpan={3}>
                  <Typography.h4>
                    {BlogID ? "Update Blog" : "Add Blog"}
                  </Typography.h4>
                </GridLayoutItem>
                <GridLayoutItem colSpan={4}>
                  <Field
                    name="Title"
                    label="Title"
                    placeholder="Title"
                    onChange={(e) => handleCreateSlug(e, formRenderProps)}
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="BlogURL"
                    label="Blog URL"
                    placeholder="Blog URL"
                    component={FormTextField}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    wrapperStyle={{ width: "100%" }}
                    name="BlogDate"
                    label="Blog Date"
                    format="dd/MM/yyyy"
                    component={FormDatePicker}
                    validator={requiredValidator}
                    astrike={true}
                  />
                </GridLayoutItem>
                <GridLayoutItem colSpan={2}>
                  <Field
                    name="ShortDescription"
                    label="Short Description"
                    placeholder="Short Description"
                    component={FormTextArea}
                    astrike={true}
                    style={{ height: 130 }}
                    validator={requiredValidator}
                  />
                  <Field
                    name="Keyword"
                    label="Keyword"
                    placeholder="Keyword"
                    component={FormTextField}
                  />
                  <Field
                    name="MetaTitle"
                    label="Meta Title"
                    placeholder="Meta Title"
                    component={FormTextField}
                  />
                  <Field
                    name="MetaDescription"
                    label="Meta Description"
                    placeholder="Meta Description"
                    component={FormTextArea}
                    style={{ height: 130 }}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={2}
                  style={{ width: "100%", height: "100%" }}
                >
                  <Field
                    name="Description"
                    label="Description"
                    component={FormRichTextField}
                    style={{ height: 365 }}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  colSpan={3}
                  style={{
                    marginTop: 20,
                    display: "flex",
                    width: "100%",
                    gap: "10px",
                  }}
                >
                  <Field
                    justifyContent="start"
                    name={`Image`}
                    label="Image"
                    type="file"
                    alternateName="Image"
                    component={FormImageUpload}
                  />
                  <Field
                    justifyContent="start"
                    name={`BlogBannerImage`}
                    label="Blog Banner Image"
                    type="file"
                    alternateName="Blog Banner Image"
                    component={FormImageUpload}
                  />
                </GridLayoutItem>

                <GridLayoutItem
                  style={{
                    marginTop: 40,
                  }}
                >
                  <Field
                    name={`IsActive`}
                    label="Status"
                    placeholder="i.e. Active"
                    component={FormSelectionField}
                    validator={requiredValidator}
                    astrike={true}
                    options={STATUSARRAY?.map((status: any) => {
                      return {
                        value: status?.value,
                        label: status?.label,
                      };
                    })}
                  />
                </GridLayoutItem>
                <GridLayoutItem
                  colSpan={4}
                  style={{
                    marginTop: "20px",
                    textAlign: "end",
                  }}
                >
                  <div>
                    <ButtonWithLoading
                      label={BlogID ? "Update & Next" : "Save & Next"}
                      type="submit"
                      disabled={!formRenderProps.allowSubmit || loading}
                      loading={loading}
                    />
                    <Button
                      type="button"
                      fillMode={"outline"}
                      themeColor={"primary"}
                      style={{ marginLeft: 10 }}
                      onClick={() => navigate("/blog")}
                    >
                      Cancel
                    </Button>
                  </div>
                </GridLayoutItem>
              </GridLayout>
            </ShadowCard>
          </FormElement>
        )}
      />
    </div>
  );
};

export default BlogDetailsForm;
